import type { ReactNode } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { AppSidebar, type NavItem } from "@trillion/ui/components/app-sidebar";
import { ErrorBoundary } from "@trillion/ui";
import { useRequireProductRole, useUser, useAuth } from "./hooks";

// Shared authenticated admin shell: role guard + sidebar + error boundary + outlet.
// Replaces the 5 near-identical `src/layouts/admin-layout.tsx`. Used as a route-layout
// element; each app passes its own appName/navItems/product/logo.
//
//   { element: <AdminShell appName="Help Admin" navItems={navItems} product="help" logo={<Logo/>} />, children: [...] }
//
// Gating is standardized on useRequireProductRole(product, "admin") — a signed-in user
// lacking the product admin role is redirected to the AuthConfig forbiddenPath ("/forbidden").

export interface AdminShellProps {
  appName: string;
  navItems: NavItem[];
  /** Product key for the admin role check + sidebar role badge (e.g. "help", "conduit"). */
  product: string;
  logo?: ReactNode;
}

export function AdminShell({ appName, navItems, product, logo }: AdminShellProps) {
  useRequireProductRole(product, "admin");
  const user = useUser();
  const { logout } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const activeHref =
    navItems.find(
      (item) => item.href === pathname || (item.href !== "/" && pathname.startsWith(item.href))
    )?.href ?? (pathname === "/" ? "/" : undefined);

  async function handleLogout() {
    await logout();
    navigate("/login");
  }

  return (
    <div className="flex h-screen">
      <AppSidebar
        appName={appName}
        logo={logo}
        items={navItems}
        activeHref={activeHref}
        LinkComponent={Link}
        user={
          user
            ? {
                name: user.displayName ?? user.email,
                email: user.email,
                role: user.productRoles?.find((r) => r.product === product)?.role,
              }
            : undefined
        }
        onLogout={handleLogout}
      />
      <main className="flex-1 overflow-auto">
        <ErrorBoundary>
          <Outlet />
        </ErrorBoundary>
      </main>
    </div>
  );
}
